import { cn } from '@/lib/cn';

/** Permukaan dasar untuk mengelompokkan konten - sesuai kartu pada rancangan. */
export const Card = ({ className, children, as: Tag = 'div', ...props }) => (
  <Tag
    className={cn(
      'rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-xs',
      className,
    )}
    {...props}
  >
    {children}
  </Tag>
);

export const CardHeader = ({ title, description, action, className, children }) => (
  <div
    className={cn(
      'flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-5 py-4',
      className,
    )}
  >
    {children ?? (
      <>
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold text-[var(--text-primary)]">{title}</h2>
          {description && (
            <p className="mt-0.5 text-sm text-[var(--text-muted)]">{description}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </>
    )}
  </div>
);

export const CardBody = ({ className, children, ...props }) => (
  <div className={cn('p-5', className)} {...props}>
    {children}
  </div>
);

export const CardFooter = ({ className, children }) => (
  <div
    className={cn(
      'flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] px-5 py-3.5',
      className,
    )}
  >
    {children}
  </div>
);

/**
 * Kartu statistik pada dashboard: ikon, nilai besar, label, dan indikator tren.
 *
 * Tren memakai warna **dan** tanda +/− sekaligus - pengguna buta warna tidak
 * bisa membedakan hijau dari merah, jadi warna saja tidak pernah cukup untuk
 * menyampaikan arti.
 */
export const StatCard = ({
  icon: Icon,
  label,
  value,
  trend,
  trendLabel,
  tone = 'neutral',
  live = false,
  loading = false,
  onClick,
}) => {
  const tones = {
    neutral: 'bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300',
    accent: 'bg-accent-50 text-accent-600 dark:bg-accent-500/15 dark:text-accent-400',
    success: 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500',
    danger: 'bg-danger-50 text-danger-600 dark:bg-danger-500/15 dark:text-danger-500',
    warning: 'bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-500',
  };

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Card
      as={Wrapper}
      onClick={onClick}
      className={cn(
        'p-4 text-left transition-shadow duration-200',
        onClick && 'hover:shadow-md focus-visible:shadow-md',
      )}
    >
      <div className="flex items-start justify-between">
        <div className={cn('grid size-9 place-items-center rounded-lg', tones[tone])}>
          {Icon && <Icon className="size-[18px]" aria-hidden="true" />}
        </div>

        {live ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-600 dark:text-accent-400">
            <span className="size-1.5 animate-pulse-dot rounded-full bg-current" />
            live
          </span>
        ) : (
          trend != null && (
            <span
              className={cn(
                'text-xs font-bold tabular',
                trend > 0
                  ? 'text-success-600 dark:text-success-500'
                  : trend < 0
                    ? 'text-danger-600 dark:text-danger-500'
                    : 'text-[var(--text-muted)]',
              )}
              title={trendLabel}
            >
              {trend > 0 ? '+' : ''}
              {trend}%
            </span>
          )
        )}
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="h-8 w-20 animate-pulse rounded-md bg-[var(--surface-sunken)]" />
        ) : (
          <p className="text-2xl font-extrabold tabular text-[var(--text-primary)]">{value}</p>
        )}
        <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">{label}</p>
      </div>
    </Card>
  );
};
